import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";

interface RouterCtx {
  pathname: string;
  navigate: (to: string) => void;
}

const Ctx = createContext<RouterCtx | null>(null);

export function Router({
  children,
  initialPath,
}: {
  children: ReactNode;
  initialPath?: string;
}) {
  // On the server there is no `window`; callers pass the request path in.
  const [pathname, setPathname] = useState(
    () => initialPath ?? (typeof window !== "undefined" ? window.location.pathname : "/"),
  );

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    if (to === window.location.pathname + window.location.search) return;
    window.history.pushState(null, "", to);
    // `to` may carry a query string (e.g. /app/orders/new?account=3) — route
    // matching is on the path alone, so read pathname back from location.
    setPathname(window.location.pathname);
  }, []);

  return <Ctx.Provider value={{ pathname, navigate }}>{children}</Ctx.Provider>;
}

export function useRouter() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRouter must be used within <Router>");
  return ctx;
}

export function Link({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  const { navigate } = useRouter();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onClick?.();
    navigate(to);
  };
  return (
    <a href={to} className={className} onClick={handle}>
      {children}
    </a>
  );
}
